import React from 'react';
import { ActivityIndicator, FlatList, Pressable, ScrollView, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from './MyAppText';
import { Hoverable } from 'react-native-web-hover';
import Divider from './Divider';
import { translate } from '../services/translate';

type ColumnBase<T> = {
    name: string;

    selector?: (row: T) => string | number | boolean;
    cell?: (row: T) => React.ReactNode;

    button?: boolean;
    center?: boolean;
    right?: boolean;
    wrap?: boolean;
};

type FixedWidthColumn<T> = ColumnBase<T> & {
    width: number;
    flex?: never;
};

type FlexColumn<T> = ColumnBase<T> & {
    flex: number;
    width?: never;
};

export type Column<T> = FixedWidthColumn<T> | FlexColumn<T>;

interface MyInfinityDataTableProps<T> {
    columns: Column<T>[];
    data: T[];
    loadMore: () => void;
    onRowPress?: (row: T) => void;
    noResultsText?: string;
    progressPending?: boolean;
    minWidth?: number;
}

export default function MyInfinityDataTable<T>(props: MyInfinityDataTableProps<T>) {
    const { styles, theme } = useStyles(stylesheet);

    const renderHeader = () => (
        <View style={styles.header}>
            {props.columns.map(col => (
                <View
                    key={col.name}
                    style={[
                        styles.cell,
                        col.center && styles.center,
                        col.right && styles.right,
                        (col.width ? { width: col.width } : null),
                        (col.flex ? { flex: col.flex } : null),
                    ]}
                >
                    <MyAppText style={styles.headerText}>{col.name}</MyAppText>
                </View>
            ))}
        </View>
    );

    const renderItem = ({ item, index }: { item: T; index: number; }) => (

        <Hoverable>
            {({ hovered }) => (
                <Pressable
                    disabled={!props.onRowPress}
                    onPress={() => props.onRowPress?.(item)}
                    style={[styles.row, {
                        backgroundColor: index % 2 === 0
                            ? hovered && props.onRowPress ? theme.colors.dataTable.rowOnHoverBackground : theme.colors.dataTable.background
                            : hovered && props.onRowPress ? theme.colors.dataTable.oddRowOnHoverBackground : theme.colors.dataTable.oddRowBackground,
                    }]}
                >
                    {props.columns.map(col => {
                        const content =
                            col.cell?.(item) ??
                            col.selector?.(item) ??
                            null;

                        return (
                            <View
                                key={col.name}
                                style={[
                                    styles.cell,
                                    col.center && styles.center,
                                    col.right && styles.right,
                                    (col.width ? { width: col.width } : null),
                                    (col.flex ? { flex: col.flex } : null),
                                ]}
                                pointerEvents={col.button ? 'box-only' : 'auto'}
                            >
                                <MyAppText>
                                    {content}
                                </MyAppText>
                            </View>
                        );
                    })}
                </Pressable>
            )}
        </Hoverable>
    );

    if (props.data.length === 0 && !props.progressPending) {
        return <MyAppText style={styles.noDataComponent}>
            {props.noResultsText ?? translate('noResults')}
            {props.noResultsText && '.'}
        </ MyAppText>;
    }

    if (props.data.length === 0 && props.progressPending) {
        return <View style={{ paddingTop: 20 }}>
            <ActivityIndicator size='small' color={theme.colors.dataTable.loading} />
        </View>;
    }

    return (<>
        <ScrollView
            horizontal={true}
            style={{ zIndex: -9999 }}
            contentContainerStyle={{ flex: 1, minWidth: props.minWidth ?? 768 }}
        >
            <FlatList
                data={props.data}
                renderItem={renderItem}
                keyExtractor={(_, i) => i.toString()}
                ListHeaderComponent={renderHeader}
                stickyHeaderIndices={[0]}
                onEndReached={props.loadMore}
                onEndReachedThreshold={0.8}
                ItemSeparatorComponent={() => <Divider color={theme.colors.container.border} />}
            />
        </ScrollView>
        {props.progressPending && <View style={{ paddingVertical: 10 }}>
            <ActivityIndicator size='small' color={theme.colors.dataTable.loading} />
        </View>}
    </>);

}

const stylesheet = createStyleSheet(theme => ({
    noDataComponent: {
        color: theme.colors.dataTable.noDataMessage,
        paddingTop: 10,
        alignSelf: 'center',
    },
    header: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderColor: theme.colors.container.border,
        height: 52,
        backgroundColor: theme.colors.container.background,
    },
    row: {
        flexDirection: 'row',
        fontSize: 14,
        minHeight: 48,
    },
    cell: {
        paddingHorizontal: 12,
        justifyContent: 'center',
    },
    headerText: {
        fontSize: 13,
        fontWeight: '600',
    },
    center: {
        alignItems: 'center',
    },
    right: {
        alignItems: 'flex-end',
    },

}));
